import { RecordCreateScreen } from "@/lib/Components/Screens/RecordScreen/RecordCreateScreen";
import { CreateRelocationInput } from "@/gql/graphql";
import { RelocationForm } from "@/app/Relocations/Forms/RelocationForm";
import { relocationValidation } from "@/app/Relocations/Validation/relocationValidation";
import { useNavigate } from "@tanstack/react-router";
import { RecordCreateCard } from "@/lib/Components/SideBarList/Cards/RecordCreateCard";
import {
  RelocationFormValues,
  getDefaultRelocationValues,
} from "@/app/Relocations/Utils/getDefaultRelocationValues";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import {
  addDestinationMutation,
  createRelocationMutation,
} from "@/app/Relocations/GraphQL/relocationMutations";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { SupplierRecord } from "@/app/Suppliers/GraphQL/supplierRecordQuery";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { useOfferTripDefaultsUpdate } from "@/app/Relocations/Hooks/useOfferTripDefaultsUpdate";
import { toast } from "sonner";

export function RelocationCreateScreen() {
  const { supplier } = useGlobalSupplier();

  return (
    <RecordCreateScreen>
      <RelocationCreateForm initialSupplier={supplier} />
    </RecordCreateScreen>
  );
}

type RelocationCreateFormProps = {
  initialSupplier?: SupplierRecord;
};
export function RelocationCreateForm({
  initialSupplier,
}: RelocationCreateFormProps) {
  const { mutateAsync: createRelocation } = useGqlMutation(
    createRelocationMutation,
  );
  const { mutateAsync: addDestinations } = useGqlMutation(
    addDestinationMutation,
  );
  const offerTripDefaultsUpdate = useOfferTripDefaultsUpdate();
  const navigation = useNavigate();

  return (
    <RecordCreateCard<RelocationFormValues>
      title="Create relocation"
      validationSchema={toFormikValidationSchema(relocationValidation)}
      onSubmit={async (values) => {
        const { deliveryOfficeIds, ...rest } = values;
        const [firstOfficeId, ...additionalOfficeIds] = deliveryOfficeIds;

        const input: CreateRelocationInput = {
          ...rest,
          deliveryOffice: {
            connect: firstOfficeId,
          },
        };

        const res = await createRelocation({ input });

        await offerTripDefaultsUpdate({
          officeAId: values.departureOffice?.connect,
          officeBId: firstOfficeId,
          values,
          awaitDialog: true,
        });

        if (additionalOfficeIds.length > 0) {
          await addDestinations({
            relocationId: res.createRelocation.id,
            officeIds: additionalOfficeIds,
          });

          toast.success(
            `${additionalOfficeIds.length} additional destination${additionalOfficeIds.length > 1 ? "s" : ""} added`,
            {
              action: {
                label: "View",
                onClick: () => {
                  navigation({
                    from: "/$tenant/$orgId/supplier/{-$supplierId}",
                    to: "/$tenant/$orgId/supplier/{-$supplierId}/relocations/$relocationId/lines",
                    params: (prev) => ({
                      ...prev,
                      relocationId: res.createRelocation.id,
                    }),
                  });
                },
              },
            },
          );
        }

        await navigation({
          from: "/$tenant/$orgId/supplier/{-$supplierId}",
          to: "/$tenant/$orgId/supplier/{-$supplierId}/relocations/$relocationId",
          params: (prev) => ({
            ...prev,
            relocationId: res.createRelocation.id,
          }),
        });
      }}
      initialValues={getDefaultRelocationValues({
        supplier: initialSupplier,
      })}
    >
      <RelocationForm />
    </RecordCreateCard>
  );
}
